/** 依 GET /status 或 /capabilities 的實際宣告解析進階工具；未宣告就不假裝可用。 */

const FILE_CAP_NAMES = ['files', 'file_upload', 'attachments'];
const ARTIFACT_CAP_NAMES = ['artifacts'];
const ANALYSIS_CAP_NAMES = ['analysis'];
const IMAGE_CAP_NAMES = ['images', 'image_generation'];
const RESEARCH_CAP_NAMES = ['research', 'deep_research'];
const IMAGE_TOOL_NAMES = ['generate_image', 'image_generation', 'images'];
const MISSING_IMAGE_STATUSES = new Set([
  'not_configured',
  'unavailable_without_provider',
  'missing_key',
  'provider_unavailable',
]);
const DEFAULT_ARTIFACT_FORMATS = ['pdf', 'docx', 'xlsx', 'pptx'];
const OPERATION_LABELS = {
  summary: '摘要',
  statistics: '統計',
  chart: '數值圖',
};
const FORMAT_LABELS = {
  pdf: 'PDF',
  docx: 'Word（DOCX）',
  xlsx: 'Excel（XLSX）',
  pptx: '簡報（PPTX）',
};

function asObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
}

function capabilitySet(status) {
  const list = status && Array.isArray(status.capabilities) ? status.capabilities : [];
  return new Set(list.filter((name) => typeof name === 'string'));
}

function featureBlock(features, keys) {
  const obj = asObject(features) || {};
  for (const key of keys) {
    if (obj[key] != null) return obj[key];
  }
  return undefined;
}

function toolEntries(status) {
  return Array.isArray(status?.tools) ? status.tools.filter((t) => t && typeof t === 'object') : [];
}

function availabilityFromBlock(block) {
  if (block === true) return { declared: true, available: true, reason: '', code: '' };
  if (block === false) return { declared: true, available: false, reason: '伺服器宣告此功能目前不可用。', code: '' };
  const obj = asObject(block);
  if (!obj) return null;
  const status = typeof obj.status === 'string' ? obj.status : '';
  const code = typeof obj.code === 'string' ? obj.code : '';
  const reason = typeof obj.reason === 'string' ? obj.reason : (typeof obj.message === 'string' ? obj.message : '');
  const configured = obj.configured;
  const availableFlag = obj.available;
  const enabledFlag = obj.enabled;
  const imageMissing = MISSING_IMAGE_STATUSES.has(status)
    || code === 'IMAGE_NOT_CONFIGURED'
    || code === 'IMAGE_PROVIDER_NOT_CONFIGURED'
    || code === 'not_configured';
  let available = true;
  if (availableFlag === false || enabledFlag === false || configured === false || imageMissing) available = false;
  else if (availableFlag === true || enabledFlag === true || configured === true || status === 'available') available = true;
  else if (status === 'unavailable' || status === 'disabled') available = false;
  const imageCode = imageMissing || (configured === false && (code === 'IMAGE_NOT_CONFIGURED' || code === 'not_configured'))
    ? (code || 'IMAGE_NOT_CONFIGURED')
    : code;
  return {
    declared: true,
    available,
    reason,
    code: imageCode,
    status,
    configured,
    formats: obj.formats,
    operations: obj.operations,
    maxUploadBytes: Number.isSafeInteger(Number(obj.max_upload_bytes)) && Number(obj.max_upload_bytes) > 0
      ? Number(obj.max_upload_bytes) : 0,
    googleDrivePublicLinks: asObject(obj.google_drive_public_links),
  };
}

function fromCaps(caps, names) {
  return names.some((name) => caps.has(name));
}

function imageToolState(status) {
  const tools = toolEntries(status);
  const hit = tools.find((tool) => IMAGE_TOOL_NAMES.includes(tool.name));
  if (!hit) return null;
  const statusName = typeof hit.status === 'string' ? hit.status : '';
  const missing = MISSING_IMAGE_STATUSES.has(statusName);
  return {
    declared: true,
    available: statusName === 'available',
    reason: typeof hit.reason === 'string' ? hit.reason : '',
    code: missing ? 'IMAGE_NOT_CONFIGURED' : '',
    status: statusName,
  };
}

function normalizeFormats(value) {
  const list = Array.isArray(value) ? value : [];
  const out = [];
  for (const item of list) {
    const id = typeof item === 'string' ? item : (item && typeof item.id === 'string' ? item.id : '');
    const key = id.trim().toLowerCase();
    if (!key || !/^[a-z0-9]{2,8}$/.test(key)) continue;
    if (out.some((x) => x.id === key)) continue;
    const label = (item && typeof item.label === 'string' && item.label.trim())
      ? item.label.trim()
      : (FORMAT_LABELS[key] || key.toUpperCase());
    out.push({ id: key, label });
  }
  return out;
}

function normalizeOperations(value) {
  const list = Array.isArray(value) ? value : [];
  const out = [];
  for (const item of list) {
    const id = typeof item === 'string' ? item : (item && typeof item.id === 'string' ? item.id : '');
    const key = id.trim().toLowerCase();
    if (!key || !/^[a-z0-9_]{2,32}$/.test(key)) continue;
    if (out.some((x) => x.id === key)) continue;
    const label = (item && typeof item.label === 'string' && item.label.trim())
      ? item.label.trim()
      : (OPERATION_LABELS[key] || key);
    out.push({ id: key, label });
  }
  return out;
}

function imageUnavailableCopy(parsed) {
  if (!parsed.declared) {
    return '伺服器未宣告圖像產生能力，因此無法使用。沒有金鑰也不會改呼叫其他供應商。';
  }
  if (parsed.code === 'IMAGE_NOT_CONFIGURED' || MISSING_IMAGE_STATUSES.has(parsed.status) || parsed.available === false) {
    if (parsed.reason && /[\u4e00-\u9fff]/.test(parsed.reason) && !/api[_-]?key|token=/i.test(parsed.reason)) {
      return parsed.reason;
    }
    return '圖像產生未設定供應商或金鑰，因此無法使用。這個環境不會改用其他供應商，也不會產生假圖。';
  }
  return '';
}

export function parseAssistantFeatures(status) {
  const caps = capabilitySet(status);
  const features = asObject(status?.features) || {};
  const filesBlock = availabilityFromBlock(featureBlock(features, FILE_CAP_NAMES));
  const artifactsBlock = availabilityFromBlock(featureBlock(features, ARTIFACT_CAP_NAMES));
  const analysisBlock = availabilityFromBlock(featureBlock(features, ANALYSIS_CAP_NAMES));
  const imageBlock = availabilityFromBlock(featureBlock(features, IMAGE_CAP_NAMES)) || imageToolState(status);
  const researchBlock = availabilityFromBlock(featureBlock(features, RESEARCH_CAP_NAMES));

  const filesDeclared = !!(filesBlock?.declared || fromCaps(caps, FILE_CAP_NAMES));
  const artifactsDeclared = !!(artifactsBlock?.declared || fromCaps(caps, ARTIFACT_CAP_NAMES));
  const analysisDeclared = !!(analysisBlock?.declared || fromCaps(caps, ANALYSIS_CAP_NAMES));
  const imagesDeclared = !!(imageBlock?.declared || fromCaps(caps, IMAGE_CAP_NAMES));
  const researchDeclared = !!(researchBlock?.declared || fromCaps(caps, RESEARCH_CAP_NAMES));

  const artifactFormats = artifactsDeclared
    ? (normalizeFormats(artifactsBlock?.formats).length
      ? normalizeFormats(artifactsBlock.formats)
      : DEFAULT_ARTIFACT_FORMATS.map((id) => ({ id, label: FORMAT_LABELS[id] })))
    : [];
  const analysisOperations = analysisDeclared
    ? normalizeOperations(analysisBlock?.operations)
    : [];

  const images = {
    declared: imagesDeclared,
    available: imagesDeclared && imageBlock ? imageBlock.available !== false && !MISSING_IMAGE_STATUSES.has(imageBlock.status) && imageBlock.code !== 'IMAGE_NOT_CONFIGURED' && imageBlock.configured !== false : (imagesDeclared && !imageBlock),
    reason: '',
    code: imageBlock?.code || '',
    status: imageBlock?.status || '',
  };
  if (imagesDeclared && imageBlock && (imageBlock.available === false || imageBlock.code === 'IMAGE_NOT_CONFIGURED' || imageBlock.configured === false || MISSING_IMAGE_STATUSES.has(imageBlock.status))) {
    images.available = false;
    if (!images.code && (imageBlock.configured === false || MISSING_IMAGE_STATUSES.has(imageBlock.status))) {
      images.code = 'IMAGE_NOT_CONFIGURED';
    }
  }
  if (!imagesDeclared) {
    images.available = false;
    images.code = '';
  }
  images.reason = imageUnavailableCopy(imagesDeclared ? { ...images, reason: imageBlock?.reason || '', status: imageBlock?.status || '' } : { declared: false });

  return {
    files: {
      declared: filesDeclared,
      available: filesDeclared && filesBlock?.available !== false,
      maxUploadBytes: filesBlock?.maxUploadBytes || 0,
      googleDrivePublicLinks: {
        available: filesBlock?.googleDrivePublicLinks?.enabled === true,
        maxLinks: Number(filesBlock?.googleDrivePublicLinks?.max_links_per_message) || 0,
        maxDownloadBytes: Number(filesBlock?.googleDrivePublicLinks?.max_download_bytes) || 0,
      },
    },
    artifacts: { declared: artifactsDeclared, available: artifactsDeclared && artifactsBlock?.available !== false, formats: artifactFormats },
    analysis: { declared: analysisDeclared, available: analysisDeclared && analysisBlock?.available !== false, operations: analysisOperations },
    images,
    research: { declared: researchDeclared, available: researchDeclared && researchBlock?.available !== false },
    hasAny: filesDeclared || artifactsDeclared || analysisDeclared || imagesDeclared || researchDeclared,
  };
}

export function mergeStatusAndCapabilities(status, caps) {
  const base = asObject(status) || {};
  const extra = asObject(caps);
  if (!extra) return base;
  const capList = [...new Set([
    ...(Array.isArray(base.capabilities) ? base.capabilities : []),
    ...(Array.isArray(extra.capabilities) ? extra.capabilities : []),
  ])].filter((name) => typeof name === 'string');
  const features = {
    ...(asObject(base.features) || {}),
    ...(asObject(extra.features) || {}),
  };
  const tools = Array.isArray(extra.tools) ? extra.tools : base.tools;
  return {
    ...base,
    ...extra,
    enabled: base.enabled,
    reason: base.reason,
    capabilities: capList,
    features,
    tools,
  };
}

export {
  DEFAULT_ARTIFACT_FORMATS,
  FILE_CAP_NAMES,
  IMAGE_CAP_NAMES,
  OPERATION_LABELS,
};
